<template>
  <div class="published-dashboard">
    <!-- 顶栏：新鲜度 + 手动刷新 -->
    <div class="dash-toolbar">
      <div class="dash-freshness">
        <span class="fresh-label">数据最新刷新</span>
        <strong>{{ formatTime(latestSyncAt || latestAsOf) || '尚未同步' }}</strong>
        <el-tag
          v-if="syncFailedCount > 0"
          type="danger"
          effect="plain"
          size="small"
          class="fail-tag"
          @click="failDialogVisible = true"
        >
          {{ syncFailedCount }} 个账号拉取失败
        </el-tag>
        <el-tag v-else-if="latestSyncAt" type="success" effect="plain" size="small">
          同步正常
        </el-tag>
      </div>
      <div class="dash-toolbar-actions">
        <el-button :loading="loading" @click="loadBoard">刷新视图</el-button>
        <el-button type="primary" :loading="syncing" @click="runManualSync">
          手动同步全部
        </el-button>
        <el-button plain @click="groupManagerVisible = true">管理预设分组</el-button>
        <el-button :disabled="!displayRows.length" @click="exportCsv">导出数据</el-button>
      </div>
    </div>

    <el-alert
      v-if="syncFailedCount > 0"
      type="error"
      :closable="false"
      show-icon
      class="dash-warn"
    >
      <template #title>
        有 {{ syncFailedCount }} 个账号数据拉取失败（部分成功 ≠ 全部成功）。
        <el-button link type="primary" @click="failDialogVisible = true">查看失败明细</el-button>
        <el-button link type="primary" :loading="syncing" @click="retryFailed">重试失败账号</el-button>
      </template>
    </el-alert>

    <!-- 筛选：改完即查，无需再点「拉取」 -->
    <div class="dash-filter-bar">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始时间"
        end-placeholder="结束时间"
        value-format="YYYY-MM-DD"
        :shortcuts="dateShortcuts"
        style="width: 280px"
        @change="onFilterChange"
      />
      <el-button-group class="quick-range">
        <el-button
          v-for="q in quickRanges"
          :key="q.days"
          :type="activeQuickDays === q.days ? 'primary' : 'default'"
          @click="applyQuickRange(q.days)"
        >{{ q.label }}</el-button>
      </el-button-group>
      <el-select
        v-model="filterPlatform"
        clearable
        placeholder="全部平台"
        style="width: 130px"
        @change="onFilterChange"
      >
        <el-option
          v-for="p in platformOptions"
          :key="p.code"
          :label="p.name"
          :value="p.code"
        />
      </el-select>
      <el-select
        v-model="filterGroup"
        clearable
        placeholder="预设分组"
        style="width: 180px"
        @change="onFilterChange"
      >
        <el-option
          v-for="g in groupOptions"
          :key="g.id"
          :label="`${g.name} (${g.count})`"
          :value="String(g.id)"
        />
        <el-option
          v-if="ungroupedCount"
          label="未入组账号"
          value="__ungrouped__"
        />
      </el-select>
      <el-button
        v-if="selectedGroupCanBoard"
        type="success"
        plain
        @click="openSelectedGroupBoard"
      >{{ selectedGroupIsXhsOnly ? '小红书看板' : '分组看板' }}</el-button>
      <el-select
        v-model="filterNoteType"
        clearable
        placeholder="全部类型"
        style="width: 120px"
        @change="onFilterChange"
      >
        <el-option label="文章" value="article" />
        <el-option label="问答/回答" value="answer" />
        <el-option label="视频" value="video" />
        <el-option label="图文" value="normal" />
      </el-select>
    </div>

    <!-- 汇总指标（含总粉丝） -->
    <div class="dash-metrics">
      <div v-for="m in metricItems" :key="m.key" class="metric-item">
        <div class="metric-label">{{ m.label }}</div>
        <div class="metric-value">{{ formatNum(summary[m.key]) }}</div>
      </div>
    </div>

    <!-- 表格工具栏 -->
    <div class="dash-table-bar">
      <el-select v-model="sortOrder" style="width: 220px" @change="onFilterChange">
        <el-option label="按阅读/播放量倒序" value="reads" />
        <el-option label="按点赞量倒序排列" value="likes" />
        <el-option label="按发布时间倒序排列" value="time_desc" />
        <el-option label="按发布时间正序排列" value="time_asc" />
      </el-select>
      <el-input
        v-model="titleKeyword"
        placeholder="请输入搜索的内容标题"
        clearable
        style="width: 260px"
        :prefix-icon="Search"
        @input="onKeywordInput"
        @change="onFilterChange"
        @clear="onFilterChange"
      />
      <span class="table-hint">共 {{ total }} 条 · 分析库直出，点击作品可看原生预览</span>
    </div>

    <el-table
      :data="pagedRows"
      v-loading="loading"
      class="dash-table"
      :empty-text="emptyText"
      stripe
      @row-click="openPreview"
    >
      <el-table-column label="账号" width="160">
        <template #default="{ row }">
          <div class="cell-account">
            <el-avatar :size="32" :src="avatarForRow(row)">
              {{ (row.account || '?')[0] }}
            </el-avatar>
            <div>
              <div
                class="cell-account-name"
                :class="{ linkish: canOpenAccountBoard(row) }"
                @click.stop="openAccountBoard(row)"
              >{{ row.account || row.account_id || '—' }}</div>
              <div v-if="row.group_name" class="cell-group">{{ row.group_name }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="平台" width="100">
        <template #default="{ row }">
          <span class="platform-badge" :class="'platform-' + row.platform">
            {{ row.platform_label || platformName(row.platform) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="作品" min-width="280">
        <template #default="{ row }">
          <div class="cell-work cell-work--text">
            <div class="work-title" :title="row.title">{{ row.title || '（无标题）' }}</div>
            <div class="work-stats work-stats--inline">
              <span v-if="row.recommend">展现 <em>{{ formatNum(row.recommend) }}</em></span>
              <span>评论 <em>{{ row.comments || 0 }}</em></span>
              <span v-if="row.platform !== 'zhihu'">分享 <em>{{ row.shares || 0 }}</em></span>
              <span>收藏 <em>{{ row.collects || row.favorites || 0 }}</em></span>
              <span>{{ row.platform === 'zhihu' ? '赞同' : '点赞' }} <em>{{ row.likes || 0 }}</em></span>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="阅读/播放" width="100" align="right">
        <template #default="{ row }">
          <span class="views-num">{{ formatNum(row.views || row.reads || 0) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="类型" width="88">
        <template #default="{ row }">
          {{ noteTypeLabel(row.note_type || row.content_type) }}
        </template>
      </el-table-column>
      <el-table-column label="发布时间" width="160">
        <template #default="{ row }">
          {{ formatPublishTime(row.published_at) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="170" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="canOpenWorkBoard(row)"
            link
            type="primary"
            @click.stop="openWorkBoard(row)"
          >看板</el-button>
          <el-button link type="primary" @click.stop="openPreview(row)">详情</el-button>
          <el-button link @click.stop="openTrend(row)">趋势</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="total > pageSize" class="dash-pagination">
      <el-pagination
        v-model:current-page="currentPage"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
        background
        @current-change="loadBoard"
      />
    </div>

    <!-- 平台原生预览 -->
    <el-dialog
      v-model="previewVisible"
      :title="previewDialogTitle"
      :width="previewDialogWidth"
      class="work-preview-dialog"
      destroy-on-close
      append-to-body
    >
      <PlatformContentViewer
        v-if="previewItem"
        :item="previewItem"
        :loading="previewLoading"
      />
    </el-dialog>

    <WorkTrendDrawer
      v-model="trendVisible"
      :work="trendItem"
    />

    <!-- 同步失败明细 -->
    <el-dialog v-model="failDialogVisible" title="同步失败明细" width="720px" destroy-on-close>
      <p class="fail-intro">以下账号/分组未拉取成功，请勿视为整轮同步已完成。</p>
      <div v-for="g in failedGroups" :key="g.group_name" class="fail-group">
        <div class="fail-group-title">
          分组：{{ g.group_name || '未分组' }}
          <el-tag type="danger" size="small" effect="plain">{{ g.count }} 个失败</el-tag>
        </div>
        <el-table :data="g.accounts" size="small" stripe>
          <el-table-column prop="display_name" label="账号" width="140" />
          <el-table-column label="平台" width="100">
            <template #default="{ row }">{{ platformName(row.platform) }}</template>
          </el-table-column>
          <el-table-column prop="error" label="失败原因" min-width="260" show-overflow-tooltip />
        </el-table>
      </div>
      <el-empty v-if="!failedGroups.length" description="当前无失败账号" :image-size="48" />
      <template #footer>
        <el-button @click="failDialogVisible = false">关闭</el-button>
        <el-button type="primary" :loading="syncing" @click="retryFailed">重试失败账号</el-button>
      </template>
    </el-dialog>

    <!-- 预设分组管理 -->
    <AccountGroupManager
      v-model="groupManagerVisible"
      :accounts="accounts"
      @changed="onGroupsChanged"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import { contentAnalyticsApi, publishV2Api } from '../api'
import PlatformContentViewer from './platform/PlatformContentViewer.vue'
import AccountGroupManager from './AccountGroupManager.vue'
import WorkTrendDrawer from './WorkTrendDrawer.vue'
import { normalizeContent, mergeContent } from './platform/normalizeContent'
import { formatBeijingDateTime } from '../utils/dateTime'

const router = useRouter()

const emit = defineEmits(['fetched', 'synced'])

const props = defineProps({
  accounts: { type: Array, default: () => [] },
  apiBase: { type: String, default: '' },
  avatarSrcFn: { type: Function, default: null },
  /** 从数据中心「管理分组」进入时自动打开分组弹窗 */
  autoOpenGroups: { type: Boolean, default: false },
})

const platformName = (code) => ({
  xiaohongshu: '小红书', douyin: '抖音', toutiao: '头条', baijiahao: '百家号',
  bilibili: 'B站', zhihu: '知乎', kuaishou: '快手', weibo: '微博', weixin: '微信',
}[code] || code)

const noteTypeLabel = (t) => {
  const map = {
    normal: '图文', video: '视频', article: '文章', answer: '回答',
    image: '图文', text: '文字', '动态': '图文', '文章': '文章', '回答': '回答', '视频': '视频',
  }
  return map[t] || (t ? String(t) : '图文')
}

const formatNum = (n) => {
  const v = Number(n) || 0
  if (v >= 10000) return `${(v / 10000).toFixed(1)}万`
  return String(v)
}

const formatTime = (value) => formatBeijingDateTime(value, '')
const formatPublishTime = (value) => formatBeijingDateTime(value)

const accountKey = (acc) => `${acc.platform}:${acc.account_id}`

const avatarForRow = (row) => {
  if (!row) return ''
  const acc = props.accounts.find(
    (a) => a.platform === row.platform && (
      a.account_id === row.account_id
      || a.display_name === row.account
    ),
  )
  if (props.avatarSrcFn && acc) return props.avatarSrcFn(acc)
  return acc?.avatar_url || ''
}

// ==================== 筛选 ====================
const dateRange = ref(null)
const activeQuickDays = ref(30)
const filterPlatform = ref('')
const filterGroup = ref('')
const filterNoteType = ref('')
const sortOrder = ref('reads')
const titleKeyword = ref('')
const currentPage = ref(1)
const pageSize = 20

const quickRanges = [
  { label: '近三天', days: 3 },
  { label: '近七天', days: 7 },
  { label: '近一个月', days: 30 },
]

const dateShortcuts = [
  { text: '近三天', value: () => rangeFromDays(3) },
  { text: '近七天', value: () => rangeFromDays(7) },
  { text: '近一个月', value: () => rangeFromDays(30) },
]

function rangeFromDays(days) {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - days + 1)
  return [fmtDate(start), fmtDate(end)]
}

function fmtDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const applyQuickRange = (days) => {
  activeQuickDays.value = days
  dateRange.value = rangeFromDays(days)
  currentPage.value = 1
  loadBoard()
}

const onFilterChange = () => {
  activeQuickDays.value = null
  currentPage.value = 1
  loadBoard({ quiet: true })
}

let keywordTimer = null
const onKeywordInput = () => {
  if (keywordTimer) clearTimeout(keywordTimer)
  keywordTimer = setTimeout(() => {
    activeQuickDays.value = null
    currentPage.value = 1
    loadBoard({ quiet: true })
  }, 350)
}

const platformOptions = [
  { code: 'xiaohongshu', name: '小红书' },
  { code: 'douyin', name: '抖音' },
  { code: 'zhihu', name: '知乎' },
  { code: 'toutiao', name: '头条' },
  { code: 'baijiahao', name: '百家号' },
  { code: 'bilibili', name: 'B站' },
  { code: 'kuaishou', name: '快手' },
  { code: 'weibo', name: '微博' },
]

// ==================== 数据 ====================
const loading = ref(false)
const syncing = ref(false)
const rawRows = ref([])
const total = ref(0)
const summary = ref({
  account_count: 0,
  work_count: 0,
  reads: 0,
  likes: 0,
  comments: 0,
  shares: 0,
  favorites: 0,
  followers: 0,
})
const latestAsOf = ref('')
const latestSyncAt = ref('')
const syncFailedCount = ref(0)
const failedGroups = ref([])
const groupOptions = ref([])
const ungroupedCount = ref(0)
const failDialogVisible = ref(false)

const metricItems = [
  { key: 'account_count', label: '账号数' },
  { key: 'followers', label: '总粉丝' },
  { key: 'work_count', label: '作品总数' },
  { key: 'reads', label: '播放/阅读量' },
  { key: 'comments', label: '评论量' },
  { key: 'shares', label: '分享量' },
  { key: 'favorites', label: '收藏量' },
  { key: 'likes', label: '点赞量' },
]

const displayRows = computed(() => rawRows.value)
const pagedRows = computed(() => rawRows.value)

const emptyText = computed(() => {
  if (loading.value) return '加载中…'
  return '分析库暂无作品。可点「手动同步全部」拉取；同步成功后会自动显示。'
})

const buildParams = () => {
  const params = {
    sort: sortOrder.value,
    limit: pageSize,
    offset: (currentPage.value - 1) * pageSize,
  }
  if (filterPlatform.value) params.platforms = filterPlatform.value
  if (filterGroup.value) params.groups = filterGroup.value
  if (filterNoteType.value) params.note_type = filterNoteType.value
  if (titleKeyword.value?.trim()) params.keyword = titleKeyword.value.trim()
  if (dateRange.value?.length === 2) {
    params.publish_start = dateRange.value[0]
    params.publish_end = dateRange.value[1]
  }
  return params
}

const pruneInvalidGroupFilter = () => {
  const cur = filterGroup.value
  if (!cur || cur === '__ungrouped__') return false
  const ok = groupOptions.value.some((g) => String(g.id) === String(cur) || g.name === cur)
  if (ok) return false
  filterGroup.value = ''
  return true
}

const loadBoard = async ({ quiet = false } = {}) => {
  if (!quiet) loading.value = true
  try {
    const data = await contentAnalyticsApi.getMatrixBoard(buildParams())
    const payload = data?.data || data
    rawRows.value = payload.items || []
    total.value = payload.total || 0
    summary.value = {
      account_count: payload.summary?.account_count || 0,
      followers: payload.summary?.followers || 0,
      work_count: payload.summary?.work_count || 0,
      reads: payload.summary?.reads || 0,
      comments: payload.summary?.comments || 0,
      shares: payload.summary?.shares || 0,
      favorites: payload.summary?.favorites || 0,
      likes: payload.summary?.likes || 0,
    }
    latestAsOf.value = payload.latest_as_of || ''
    latestSyncAt.value = payload.latest_sync_at || ''
    syncFailedCount.value = payload.sync?.failed_count || 0
    failedGroups.value = payload.sync?.failed_groups || []
    groupOptions.value = payload.groups?.items || []
    ungroupedCount.value = payload.groups?.ungrouped || 0
    emit('fetched', { items: rawRows.value, total: total.value, summary: summary.value })
    if (pruneInvalidGroupFilter()) {
      await loadBoard({ quiet: true })
      return
    }
  } catch (e) {
    if (!quiet) {
      ElMessage.error(e?.response?.data?.detail || e?.message || '加载失败')
    }
  } finally {
    if (!quiet) loading.value = false
  }
}

const showSyncResult = async (res) => {
  const data = res?.data || res || {}
  const failed = data.failed || 0
  const success = data.success || 0
  const totalN = data.total || 0
  const failedAccounts = data.failed_accounts || data.last_round?.failed_accounts || []
  const groups = data.failed_groups || []

  if (failed > 0) {
    failedGroups.value = groups.length
      ? groups
      : [{
          group_name: '失败账号',
          count: failedAccounts.length || failed,
          accounts: failedAccounts,
        }]
    syncFailedCount.value = failed
    failDialogVisible.value = true
    await ElMessageBox.alert(
      `同步结束：成功 ${success}/${totalN}，失败 ${failed}。部分成功不能当作全部成功，请查看失败明细。`,
      '同步部分失败',
      { type: 'warning', confirmButtonText: '查看明细' },
    )
  } else {
    ElMessage.success(`同步完成：成功 ${success}/${totalN}，作品 ${data.works || 0}`)
  }
  emit('synced', data)
}

const pollUntilIdle = async () => {
  for (let i = 0; i < 90; i++) {
    await new Promise((r) => setTimeout(r, 2000))
    const st = await publishV2Api.getDataSyncStatus()
    const state = (st?.data || st)?.state || {}
    try {
      await loadBoard({ quiet: true })
    } catch (_) { /* ignore */ }
    if (!state.running) {
      const round = state.last_round || {}
      return {
        ...(st?.data || st),
        ...round,
        failed_accounts: round.failed_accounts || [],
        failed_groups: round.failed_groups || [],
      }
    }
  }
  return { failed: 0, success: 0, total: 0, timeout: true }
}

const runManualSync = async () => {
  syncing.value = true
  try {
    const res = await publishV2Api.runDataSync({ trigger: 'manual' })
    const data = res?.data || res
    if (data?.running || data?.started || data?.ok) {
      ElMessage.info('已开始后台同步，完成后自动刷新…')
      const finalRes = await pollUntilIdle()
      if (finalRes.timeout) {
        ElMessage.warning('同步仍在进行，请稍后手动刷新视图')
      } else {
        await showSyncResult(finalRes)
      }
      await loadBoard()
    } else if (data?.error) {
      ElMessage.error(data.error)
    } else {
      await showSyncResult(data)
      await loadBoard()
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || e?.message || '同步失败')
  } finally {
    syncing.value = false
  }
}

const retryFailed = async () => {
  syncing.value = true
  try {
    await publishV2Api.retryFailedDataSync()
    ElMessage.info('已开始重试失败账号…')
    const finalRes = await pollUntilIdle()
    await showSyncResult(finalRes)
    await loadBoard()
    failDialogVisible.value = syncFailedCount.value > 0
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || e?.message || '重试失败')
  } finally {
    syncing.value = false
  }
}

// ==================== 分组管理 ====================
const groupManagerVisible = ref(false)

watch(
  () => props.autoOpenGroups,
  (v) => {
    if (v) groupManagerVisible.value = true
  },
  { immediate: true },
)

const onGroupsChanged = async () => {
  groupManagerVisible.value = false
  await loadBoard({ quiet: true })
}

const onVisibilityRefresh = () => {
  if (document.visibilityState === 'visible' && !syncing.value) {
    loadBoard({ quiet: true })
  }
}

// ==================== 预览 ====================
const previewVisible = ref(false)
const previewLoading = ref(false)
const previewItem = ref(null)
const trendVisible = ref(false)
const trendItem = ref(null)

const openTrend = (row) => {
  if (!row) return
  trendItem.value = row
  trendVisible.value = true
}

const selectedGroupCanBoard = computed(() => {
  const cur = filterGroup.value
  if (!cur || cur === '__ungrouped__') return false
  const g = groupOptions.value.find((x) => String(x.id) === String(cur))
  if (!g) return false
  return (g.accounts || []).some(
    (a) => BOARD_PLATFORMS.includes(a.platform),
  )
})

const selectedGroupIsXhsOnly = computed(() => {
  const cur = filterGroup.value
  if (!cur || cur === '__ungrouped__') return false
  const g = groupOptions.value.find((x) => String(x.id) === String(cur))
  if (!g) return false
  const board = (g.accounts || []).filter(
    (a) => BOARD_PLATFORMS.includes(a.platform),
  )
  return board.length > 0 && board.every((a) => a.platform === 'xiaohongshu')
})

const openSelectedGroupBoard = () => {
  const cur = filterGroup.value
  if (!cur || cur === '__ungrouped__') return
  // 纯小红书组：进原来的小红书分组看板
  if (selectedGroupIsXhsOnly.value) {
    router.push({ path: `/data-analytics/xhs/group/${cur}`, query: { range: '7' } })
    return
  }
  router.push({ path: `/data-analytics/group/${cur}`, query: { range: '7' } })
}

const BOARD_PLATFORMS = ['xiaohongshu', 'zhihu', 'douyin', 'toutiao', 'baijiahao']
const BOARD_PATH = {
  xiaohongshu: 'xhs',
  zhihu: 'zhihu',
  douyin: 'douyin',
  toutiao: 'toutiao',
  baijiahao: 'baijiahao',
}

const resolveBoardAccountKey = (row) => {
  const platform = String(row?.platform || '').trim()
  if (!BOARD_PLATFORMS.includes(platform)) return ''
  const uid = String(row?.external_user_id || '').trim()
  if (uid) return uid
  const accountId = row?.account_id || ''
  const acc = (props.accounts || []).find(
    (a) => a.platform === platform && (
      a.account_id === accountId || a.display_name === row.account
    ),
  )
  return String(acc?.external_user_id || accountId || '').trim()
}

const canOpenAccountBoard = (row) => !!resolveBoardAccountKey(row)

const canOpenWorkBoard = (row) => {
  const platform = String(row?.platform || '')
  if (!BOARD_PLATFORMS.includes(platform)) return false
  if (platform === 'xiaohongshu') return !!(row?.work_id || resolveBoardAccountKey(row))
  return !!resolveBoardAccountKey(row)
}

const openAccountBoard = (row) => {
  const platform = String(row?.platform || '')
  const key = resolveBoardAccountKey(row)
  const seg = BOARD_PATH[platform]
  if (!key || !seg) return
  const query = { range: '7' }
  const gid = String(row?.group_id || (filterGroup.value !== '__ungrouped__' ? filterGroup.value : '') || '').trim()
  if (gid) {
    query.group = gid
    query.fromGroup = gid
    if (row?.group_name) query.groupName = row.group_name
  }
  router.push({ path: `/data-analytics/${seg}/${encodeURIComponent(key)}`, query })
}

const openWorkBoard = (row) => {
  const platform = String(row?.platform || '')
  const key = resolveBoardAccountKey(row)
  if (!key) return
  const query = { range: '7' }
  const gid = String(row?.group_id || (filterGroup.value !== '__ungrouped__' ? filterGroup.value : '') || '').trim()
  if (gid) {
    query.group = gid
    query.fromGroup = gid
    if (row?.group_name) query.groupName = row.group_name
  }
  // 目前仅小红书/抖音有单作品看板；其它平台先进账号看板
  if (platform === 'xiaohongshu' && row?.work_id) {
    router.push({
      path: `/data-analytics/xhs/${encodeURIComponent(key)}/notes/${row.work_id}`,
      query,
    })
    return
  }
  if (platform === 'douyin' && (row?.work_id || row?.item_id)) {
    const itemId = row.work_id || row.item_id
    router.push({
      path: `/data-analytics/douyin/${encodeURIComponent(key)}/works/${encodeURIComponent(itemId)}`,
      query,
    })
    return
  }
  openAccountBoard(row)
}

const previewDialogTitle = computed(() => {
  const p = previewItem.value?.platform
  const name = platformName(p)
  const t = noteTypeLabel(previewItem.value?.note_type || previewItem.value?.content_type)
  return name ? `${name} · ${t}预览` : '内容预览'
})

const previewDialogWidth = computed(() => {
  const p = previewItem.value?.platform
  if (p === 'zhihu') return '760px'
  if (p === 'douyin') return '420px'
  return '440px'
})

const openPreview = async (row) => {
  if (!row) return
  const platform = row.platform || ''
  const accountId = row.account_id || ''
  const acc = props.accounts.find(
    (a) => a.platform === platform && (
      a.account_id === accountId || a.display_name === row.account
    ),
  )
  const resolvedId = accountId || acc?.account_id || ''
  const base = normalizeContent(
    {
      ...row,
      platform,
      note_id: row.note_id || row.work_id,
      content: row.content || row.desc || '',
    },
    { platform, account: acc, proxyUrl: publishV2Api.coverProxyUrl },
  )
  previewItem.value = base
  previewVisible.value = true

  if (
    !['xiaohongshu', 'douyin', 'zhihu', 'bilibili', 'weibo'].includes(platform)
    || !resolvedId
    || !(row.note_id || row.work_id)
  ) {
    return
  }

  previewLoading.value = true
  try {
    const detail = await publishV2Api.getWorkDetail(
      platform,
      resolvedId,
      row.note_id || row.work_id,
      {
        note_url: row.source_note_url || row.note_url || row.link || undefined,
        note_type: row.note_type || row.content_type || undefined,
      },
    )
    const enriched = normalizeContent(
      { ...row, ...detail, platform: detail.platform || platform },
      { platform, account: acc, proxyUrl: publishV2Api.coverProxyUrl },
    )
    previewItem.value = mergeContent(base, enriched)
  } catch (e) {
    const msg = e?.response?.data?.detail || e?.message || '详情补全失败'
    previewItem.value = {
      ...base,
      detailWarning: typeof msg === 'string' ? msg : '详情补全失败，已保留列表预览',
    }
  } finally {
    previewLoading.value = false
  }
}

const exportCsv = () => {
  const header = ['账号', '分组', '平台', '标题', '类型', '发布时间', '阅读', '推荐', '评论', '分享', '收藏', '点赞', '链接']
  const lines = [header.join(',')]
  for (const r of displayRows.value) {
    const cols = [
      r.account || r.account_id,
      r.group_name || '',
      platformName(r.platform),
      `"${(r.title || '').replace(/"/g, '""')}"`,
      noteTypeLabel(r.note_type || r.content_type),
      formatPublishTime(r.published_at),
      r.views || r.reads || 0,
      r.recommend || 0,
      r.comments || 0,
      r.shares || 0,
      r.collects || r.favorites || 0,
      r.likes || 0,
      r.note_url || r.link || '',
    ]
    lines.push(cols.join(','))
  }
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `矩阵作品_${fmtDate(new Date())}.csv`
  a.click()
  URL.revokeObjectURL(a.href)
}

onMounted(() => {
  applyQuickRange(30)
  document.addEventListener('visibilitychange', onVisibilityRefresh)
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibilityRefresh)
  if (keywordTimer) clearTimeout(keywordTimer)
})

defineExpose({
  loadBoard,
  queryWorks: loadBoard,
  runManualSync,
  displayRows,
  loading,
  getRowsForSync: () => displayRows.value,
})
</script>

<style scoped>
.published-dashboard {
  background: #fff;
  border-radius: 8px;
  padding: 4px 0 16px;
}

.dash-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 14px;
  background: #f7f9fc;
  border-radius: 8px;
}

.dash-freshness {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #606266;
}

.fresh-label { color: #909399; }

.dash-freshness strong {
  color: #303133;
  font-weight: 600;
}

.fail-tag { cursor: pointer; }

.dash-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dash-warn { margin-bottom: 12px; }

.dash-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.quick-range :deep(.el-button) {
  padding: 8px 14px;
}

.dash-metrics {
  display: flex;
  flex-wrap: wrap;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 16px;
}

.metric-item {
  flex: 1;
  min-width: 100px;
  text-align: center;
  padding: 16px 8px;
  border-right: 1px solid #ebeef5;
}

.metric-item:last-child { border-right: none; }

.metric-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.metric-value {
  font-size: 22px;
  font-weight: 600;
  color: #303133;
  line-height: 1.2;
}

.dash-table-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.table-hint {
  font-size: 12px;
  color: #909399;
}

.dash-table { width: 100%; }
.dash-table :deep(.el-table__row) { cursor: pointer; }

.cell-account {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cell-account-name {
  font-size: 13px;
  color: #303133;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-account-name.linkish {
  color: var(--mp-accent);
  cursor: pointer;
}
.cell-account-name.linkish:hover {
  text-decoration: underline;
}

.cell-group {
  font-size: 11px;
  color: #909399;
}

.platform-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: #f0f2f5;
  color: #606266;
}

.platform-zhihu { background: #eef3ff; color: #4f78e9; }
.platform-xiaohongshu { background: #fff0f2; color: #ef6270; }
.platform-douyin { background: #f0f2f5; color: #202838; }
.platform-toutiao { background: #fff4ef; color: #ff6a3d; }

.work-title {
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.work-stats--inline {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  color: #909399;
}

.work-stats--inline em {
  font-style: normal;
  color: #606266;
  font-weight: 600;
}

.views-num {
  font-weight: 600;
  color: #303133;
}

.dash-pagination {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}

.fail-intro {
  margin: 0 0 12px;
  color: #606266;
  font-size: 13px;
}

.fail-group { margin-bottom: 16px; }

.fail-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 600;
  color: #303133;
}
</style>
